import React, { useState } from "react";
import {
  Clock,
  ShieldAlert,
  RefreshCw,
  LogOut,
  CheckCircle2,
} from "lucide-react";
import { UserProfile } from "../types";

export interface PendingApprovalViewProps {
  user: UserProfile | null;
  onRefresh?: () => Promise<void> | void;
  onSignOut?: () => Promise<void> | void;
}

export const PendingApprovalView: React.FC<PendingApprovalViewProps> = ({
  user,
  onRefresh,
  onSignOut,
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    if (!onRefresh) return;
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div className="max-w-md w-full glass rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
        {/* Top Accent Banner */}
        <div className="bg-amber-500/10 border-b border-amber-200 p-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-500 text-white flex items-center justify-center shadow-md shadow-amber-500/20 shrink-0">
            <Clock className="w-6 h-6 animate-pulse" />
          </div>
          <div>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
              <ShieldAlert className="w-3.5 h-3.5" />
              Admin Verification Required
            </span>
            <h2 className="text-lg font-bold text-slate-900 mt-1">
              Pending Approval
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* User Details Pill */}
          {user && (
            <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-100/80 border border-slate-200">
              {user.photoURL ? (
                <img
                  src={user.photoURL}
                  alt={user.displayName}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-teal-600 text-white font-bold flex items-center justify-center text-sm shadow-sm">
                  {user.displayName.slice(0, 2).toUpperCase()}
                </div>
              )}
              <div className="overflow-hidden">
                <p className="text-sm font-semibold text-slate-800 truncate">
                  {user.displayName}
                </p>
                <p className="text-xs text-slate-500 truncate">{user.email}</p>
              </div>
            </div>
          )}

          <p className="text-sm text-slate-600 leading-relaxed">
            Welcome to{" "}
            <span className="font-semibold text-slate-800">MLC Ticketing</span>.
            Your Google Workspace account was authenticated successfully, but an
            IT Administrator must approve your account and assign your
            application role before you can access the platform.
          </p>

          {/* Steps */}
          <div className="space-y-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm text-xs text-slate-600">
            <div className="flex items-start gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
              <span>Google SSO identity verified (@mlconnections.com)</span>
            </div>
            <div className="flex items-start gap-2.5">
              <Clock className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <span>IT / HR Administrator approval in queue</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3 pt-2">
            <button
              type="button"
              onClick={() => void handleRefresh()}
              disabled={isRefreshing}
              className="btn-animate w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-semibold shadow-md shadow-teal-600/20 hover:bg-teal-700 disabled:opacity-50"
            >
              <RefreshCw
                className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
              />
              <span>
                {isRefreshing ? "Checking Status..." : "Check Status"}
              </span>
            </button>

            {onSignOut && (
              <button
                type="button"
                onClick={() => void onSignOut()}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Sign Out</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
